import React, { useCallback, useRef } from "react";
import { Settings2 } from "lucide-react";

import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Hint } from "@/components/ui/hint";
import { useSlideStore } from "@/stores/slide-store";
import type { PresentationConfig } from "@/stores/slide-store";
import { claudeAgentApi } from "@/lib/api/claude-agent";
import { previewMessage } from "@/lib/visual-editor/post-message";
import { useSlideAspectRatio } from "@/hooks/use-slide-aspect-ratio";

interface PresentationSettingsProps {
  previewIframeRef: React.RefObject<HTMLIFrameElement | null>;
  devContainerId?: string;
  getIdToken: () => Promise<string | null>;
  // Required for cross-window broadcast to the presenter dashboard.
  // Without it, the presenter window won't see config edits live and
  // the user has to refresh it (because we deliberately stopped the
  // presenter's 5s manifest poll from syncing session-controllable
  // fields — that poll would otherwise clobber the live ▶/⏸ state).
  projectId?: string;
}

export function PresentationSettings({
  previewIframeRef,
  devContainerId,
  getIdToken,
  projectId,
}: PresentationSettingsProps) {
  const { presentationConfig, setPresentationConfig } = useSlideStore();
  const saveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Same write-path as the viewport dropdown: optimistic store update +
  // postMessage + serialized PUT with rollback. Routing both UIs through the
  // hook keeps slides.json the single source of truth and avoids duplicating
  // the failure/rollback logic across surfaces.
  const { aspectRatio, setAspectRatio } = useSlideAspectRatio({
    devContainerId,
    previewIframeRef,
  });

  const debouncedSave = useCallback(
    (config: Partial<PresentationConfig>) => {
      setPresentationConfig(config);

      const fullConfig = {
        ...useSlideStore.getState().presentationConfig,
        ...config,
      };
      previewMessage.updatePresentationConfig(
        previewIframeRef.current,
        fullConfig as Record<string, boolean | number | string>,
      );

      // Cross-window: tell the presenter dashboard about the change so
      // the user doesn't have to refresh it. Same channel + message
      // shape used by the in-window ▶/⏸ flow; the presenter's listener
      // applies the partial config to its own store and forwards to the
      // template iframe. BC is no-op when no one's listening (presenter
      // not open).
      if (projectId) {
        try {
          const bc = new BroadcastChannel(
            `optidev-slide-sync:${projectId}`,
          );
          bc.postMessage({ type: "presentation-config-update", config });
          bc.close();
        } catch {
          // Browsers without BroadcastChannel (or in odd contexts):
          // the presenter just won't sync live — user can refresh.
        }
      }

      if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
      saveTimerRef.current = setTimeout(async () => {
        if (!devContainerId) return;
        try {
          const token = await getIdToken();
          if (!token) return;
          const currentConfig = useSlideStore.getState().presentationConfig;
          await claudeAgentApi.updateSlidesConfig(
            token,
            devContainerId,
            currentConfig,
          );
        } catch (err) {
          console.error("Failed to save presentation config:", err);
        }
      }, 500);
    },
    [devContainerId, getIdToken, setPresentationConfig, projectId],
  );

  return (
    <Popover>
      <Hint
        delayDuration={0}
        text="Presentation settings"
        side="right"
        align="center"
      >
        <PopoverTrigger asChild>
          <button className="p-1 hover:bg-accent rounded transition-colors">
            <Settings2 className="w-3.5 h-3.5 text-muted-foreground" />
          </button>
        </PopoverTrigger>
      </Hint>
      <PopoverContent side="right" align="start" className="w-56 p-0">
        <div className="p-3 space-y-3">
          <p className="text-xs font-medium text-foreground">Presentation</p>

          {/* Slide Size */}
          <div className="space-y-2">
            <p className="text-[10px] font-medium text-muted-foreground uppercase tracking-wider">
              Slide Size
            </p>
            <SettingRow label="Aspect ratio">
              <Select
                value={aspectRatio}
                onValueChange={(value: string) => {
                  if (value === "16:9" || value === "4:3") {
                    void setAspectRatio(value);
                  }
                }}
              >
                <SelectTrigger className="h-6 w-[90px] text-[11px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="16:9">16:9 Widescreen</SelectItem>
                  <SelectItem value="4:3">4:3 Standard</SelectItem>
                </SelectContent>
              </Select>
            </SettingRow>
          </div>

          <div className="border-t border-border" />

          {/* Playback */}
          <div className="space-y-2">
            <p className="text-[10px] font-medium text-muted-foreground uppercase tracking-wider">
              Playback
            </p>
            <SettingRow label="Autoplay">
              <Switch
                checked={presentationConfig.autoplay}
                onCheckedChange={(checked: boolean) =>
                  debouncedSave({ autoplay: checked })
                }
                className="scale-75 origin-right"
              />
            </SettingRow>
            <SettingRow label="Interval">
              <CompactNumberInput
                value={presentationConfig.interval / 1000}
                onChange={(val) => debouncedSave({ interval: Math.round(val * 1000) })}
                min={1}
                max={60}
                step={1}
                unit="s"
                disabled={!presentationConfig.autoplay}
              />
            </SettingRow>
            <SettingRow label="Loop">
              <Switch
                checked={presentationConfig.loop}
                onCheckedChange={(checked: boolean) =>
                  debouncedSave({ loop: checked })
                }
                className="scale-75 origin-right"
              />
            </SettingRow>
          </div>

          <div className="border-t border-border" />

          {/* Transitions */}
          <div className="space-y-2">
            <p className="text-[10px] font-medium text-muted-foreground uppercase tracking-wider">
              Transition
            </p>
            <SettingRow label="Type">
              <Select
                value={presentationConfig.transition}
                onValueChange={(value: string) =>
                  debouncedSave({
                    transition: value as PresentationConfig["transition"],
                  })
                }
              >
                <SelectTrigger className="h-6 w-[90px] text-[11px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <TransitionSelectItems />
                </SelectContent>
              </Select>
            </SettingRow>
            <SettingRow label="Duration">
              <CompactNumberInput
                value={presentationConfig.transitionDuration}
                onChange={(val) => debouncedSave({ transitionDuration: val })}
                min={0}
                max={2000}
                step={50}
                unit="ms"
              />
            </SettingRow>
          </div>

          <div className="border-t border-border" />

          {/* Controls */}
          <div className="space-y-2">
            <p className="text-[10px] font-medium text-muted-foreground uppercase tracking-wider">
              Controls
            </p>
            <SettingRow label="Show dots">
              <Switch
                checked={presentationConfig.showControls}
                onCheckedChange={(checked: boolean) =>
                  debouncedSave({ showControls: checked })
                }
                className="scale-75 origin-right"
              />
            </SettingRow>
            <SettingRow label="Pause on touch">
              <Switch
                checked={presentationConfig.pauseOnInteraction}
                onCheckedChange={(checked: boolean) =>
                  debouncedSave({ pauseOnInteraction: checked })
                }
                disabled={!presentationConfig.autoplay}
                className="scale-75 origin-right"
              />
            </SettingRow>
            <SettingRow label="Resume delay">
              <CompactNumberInput
                value={presentationConfig.resumeDelay / 1000}
                onChange={(val) => debouncedSave({ resumeDelay: Math.round(val * 1000) })}
                min={1}
                max={60}
                step={1}
                unit="s"
                disabled={
                  !presentationConfig.autoplay ||
                  !presentationConfig.pauseOnInteraction
                }
              />
            </SettingRow>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}

function SettingRow({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div className="flex items-center justify-between">
      <Label className="text-xs font-normal text-foreground">{label}</Label>
      {children}
    </div>
  );
}

/** Compact number input with unit label — reused across settings popovers */
export function CompactNumberInput({
  value,
  onChange,
  min,
  max,
  step = 1,
  unit,
  disabled,
}: {
  value: number;
  onChange: (value: number) => void;
  min: number;
  max: number;
  step?: number;
  unit: string;
  disabled?: boolean;
}) {
  return (
    <div className="flex items-center gap-1">
      <Input
        type="number"
        min={min}
        max={max}
        step={step}
        value={value}
        onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
          const val = parseFloat(e.target.value);
          if (!isNaN(val) && val >= min && val <= max) {
            onChange(val);
          }
        }}
        className="h-6 w-12 text-[11px] text-center px-1 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
        disabled={disabled}
      />
      <span className="text-[10px] text-muted-foreground w-4">{unit}</span>
    </div>
  );
}

/** Shared transition select items — used by global and per-slide settings */
export function TransitionSelectItems() {
  return (
    <>
      <SelectItem value="fade">Fade</SelectItem>
      <SelectItem value="dissolve">Dissolve</SelectItem>
      <SelectItem value="slide">Slide</SelectItem>
      <SelectItem value="slide-up">Slide Up</SelectItem>
      <SelectItem value="slide-down">Slide Down</SelectItem>
      <SelectItem value="zoom">Zoom</SelectItem>
      <SelectItem value="wipe">Wipe</SelectItem>
      <SelectItem value="wipe-up">Wipe Up</SelectItem>
      <SelectItem value="wipe-down">Wipe Down</SelectItem>
      <SelectItem value="wipe-tl">Wipe Top-Left</SelectItem>
      <SelectItem value="wipe-tr">Wipe Top-Right</SelectItem>
      <SelectItem value="wipe-bl">Wipe Bottom-Left</SelectItem>
      <SelectItem value="wipe-br">Wipe Bottom-Right</SelectItem>
      <SelectItem value="flip">Flip</SelectItem>
      <SelectItem value="cube">Cube</SelectItem>
      <SelectItem value="none">None</SelectItem>
    </>
  );
}
